import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useApp } from "@/lib/store";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, Plus, ArrowRight, Sparkles } from "lucide-react";

const HERO_IMAGE = "/assets/plates/special-plate.jpg";

export default function MenuPage() {
  const { t } = useApp();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [restaurantInfo, setRestaurantInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [heroImages, setHeroImages] = useState([HERO_IMAGE]);
  const [activeHeroIndex, setActiveHeroIndex] = useState(0);

  useEffect(() => {
    const load = async () => {
      try {
        const [catRes, prodRes, infoRes] = await Promise.all([
          api.get("/menu/categories"),
          api.get("/menu/products"),
          api.get("/menu/restaurant-info")
        ]);
        setCategories(catRes.data);
        setProducts(prodRes.data);
        setRestaurantInfo(infoRes.data || null);
        if (catRes.data.length > 0) setActiveCategory(catRes.data[0].id);
      } catch (e) {
        if (process.env.NODE_ENV !== "test") {
          console.error("Failed to load menu:", e);
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filteredProducts = activeCategory
    ? products.filter(p => p.category_id === activeCategory)
    : products;

  useEffect(() => {
    const dishImages = [...new Set(products.map((p) => p.image_url).filter(Boolean))];
    const fallbackImage = restaurantInfo?.site_hero_image || HERO_IMAGE;
    const pool = dishImages.length > 0 ? dishImages : [fallbackImage];
    setHeroImages(pool);
    setActiveHeroIndex(Math.floor(Math.random() * pool.length));
  }, [products, restaurantInfo?.site_hero_image]);

  useEffect(() => {
    if (heroImages.length <= 1) return;

    const timer = setInterval(() => {
      setActiveHeroIndex((current) => {
        if (heroImages.length === 2) return current === 0 ? 1 : 0;
        let next = current;
        while (next === current) {
          next = Math.floor(Math.random() * heroImages.length);
        }
        return next;
      });
    }, 4500);

    return () => clearInterval(timer);
  }, [heroImages]);

  const heroImage = heroImages[activeHeroIndex] || restaurantInfo?.site_hero_image || HERO_IMAGE;

  // First burger = featured
  const featuredProduct = products.find(p => p.name?.includes("Truffle"));

  return (
    <div data-testid="menu-page" className="relative z-[1]">
      {/* ===== Hero — cinematic crossfade ===== */}
      <section className="keep-dark relative h-[58vh] min-h-[400px] overflow-hidden -mt-16 md:-mt-20">
        <AnimatePresence mode="wait">
          <motion.img
            key={heroImage}
            src={heroImage}
            alt="Bästa Biten"
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.1, ease: "easeInOut" }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/75 via-[#0A0A0A]/40 to-[#0A0A0A]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_58%_52%_at_50%_55%,rgba(10,10,10,0.66),rgba(10,10,10,0.25)_68%,transparent_100%)]" />
        <div className="absolute inset-0 noise-overlay" />

        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-5 pt-12">
          <motion.div initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}>
            <span className="lux-eyebrow justify-center">{t("Menu heading")}</span>
            <h1 className="font-playfair text-[clamp(2.6rem,9vw,5rem)] text-[#F5F1E8] leading-none mt-5 lux-title">
              Bästa <em className="text-gold font-medium">Biten</em>
            </h1>
            <p className="text-[#D4D4D8] mt-5 tracking-[0.32em] uppercase text-[10px] font-medium">
              Modern Nordic Cuisine · Olofström
            </p>
          </motion.div>
        </div>
      </section>

      {/* ===== Featured — Chef's Pick ===== */}
      {featuredProduct && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 pt-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.6 }}>
            <div className="flex items-center gap-2.5 mb-5">
              <Flame className="w-4 h-4 text-[#D4B483]" />
              <h2 className="text-[10px] uppercase tracking-[0.36em] text-[#D4B483] font-semibold">{t("Chef's Pick")}</h2>
              <span className="h-px flex-1 bg-gradient-to-r from-[#D4B483]/30 to-transparent" />
            </div>
            <Link
              to={featuredProduct.out_of_stock ? "#" : `/product/${featuredProduct.id}`}
              onClick={(e) => featuredProduct.out_of_stock && e.preventDefault()}
              data-testid="featured-product-card"
            >
              <div className="keep-dark lux-frame relative rounded-2xl overflow-hidden group">
                <div className="overflow-hidden">
                  {featuredProduct.image_url ? (
                    <img
                      src={featuredProduct.image_url}
                      alt={featuredProduct.name}
                      className="w-full h-60 md:h-[22rem] object-cover group-hover:scale-[1.05] transition-transform duration-[1.2s] ease-out"
                    />
                  ) : (
                    <div className="w-full h-60 md:h-[22rem] bg-[#1A1A1A]" />
                  )}
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/35 to-transparent" />
                <div className="absolute top-5 left-5 z-[3]">
                  {featuredProduct.out_of_stock ? (
                    <Badge className="bg-[#DC2626] text-white font-bold text-[10px] tracking-wider px-3 py-1">
                      {t("Out of stock")}
                    </Badge>
                  ) : (
                    <Badge className="bg-[#D4B483] text-[#0A0A0A] font-bold text-[10px] tracking-[0.14em] badge-glow px-3.5 py-1.5 rounded-full">
                      <Sparkles className="w-3 h-3 mr-1.5" /> {t("TOP PICK")}
                    </Badge>
                  )}
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 z-[3]">
                  <h3 className="font-playfair text-3xl md:text-4xl text-[#F5F1E8]">{featuredProduct.name}</h3>
                  <p className="text-[#D4D4D8] text-sm mt-2 line-clamp-2 max-w-lg font-light">{featuredProduct.description}</p>
                  <div className="flex items-center justify-between mt-4">
                    <p className="font-mono-price text-gold text-2xl">{featuredProduct.base_price.toFixed(0)} kr</p>
                    <div className="flex items-center gap-2 text-[#D4B483] text-xs uppercase tracking-[0.18em] font-semibold opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-400">
                      <span>{t("Order now")}</span>
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
        </section>
      )}

      {/* ===== Categories — editorial tabs ===== */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pt-14">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
          <div className="flex gap-7 md:gap-9 overflow-x-auto no-scrollbar pb-1 border-b border-[#1F1F22]" data-testid="category-tabs">
            {loading ? (
              Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-8 w-28 rounded-md flex-shrink-0 mb-3" />)
            ) : (
              categories.map((cat, i) => (
                <motion.button
                  key={cat.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + i * 0.06 }}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`relative pb-4 text-[12px] font-bold uppercase tracking-[0.18em] whitespace-nowrap flex-shrink-0 transition-colors duration-300 ${
                    activeCategory === cat.id
                      ? "text-[#D4B483]"
                      : "text-[#B4B4BC] hover:text-[#EDEDED]"
                  }`}
                  data-testid={`category-btn-${cat.id}`}
                >
                  {cat.name}
                  <span
                    className={`absolute left-0 right-0 bottom-[-1px] h-[2px] rounded-full bg-gradient-to-r from-[#D4B483] to-[#B18B52] transition-transform duration-300 origin-left ${
                      activeCategory === cat.id ? "scale-x-100" : "scale-x-0"
                    }`}
                  />
                </motion.button>
              ))
            )}
          </div>
        </motion.div>
      </section>

      {/* ===== Active category header ===== */}
      {!loading && activeCategory && (() => {
        const activeCat = categories.find(c => c.id === activeCategory);
        if (!activeCat?.image_url) return null;
        return (
          <section className="max-w-7xl mx-auto px-4 md:px-8 pt-8">
            <motion.div
              key={activeCat.id + "-header"}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="keep-dark lux-frame relative rounded-2xl overflow-hidden h-40 md:h-52"
            >
              <img src={activeCat.image_url} alt={activeCat.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-[#0A0A0A]/55" />
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 z-[3]">
                <h3 className="font-playfair text-3xl md:text-5xl text-[#F5F1E8]">{activeCat.name}</h3>
                {activeCat.description && (
                  <p className="text-[#C9C9CE] text-xs md:text-sm mt-2.5 italic font-light tracking-wide line-clamp-2 max-w-md">{activeCat.description}</p>
                )}
              </div>
            </motion.div>
          </section>
        );
      })()}

      {/* ===== Products grid ===== */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-10 pb-20">
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-7">
            {Array(6).fill(0).map((_, i) => (
              <div key={i} className="rounded-2xl overflow-hidden">
                <Skeleton className="aspect-[4/3] w-full" />
                <div className="p-4 space-y-2 bg-[#121212]">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-5 w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={activeCategory}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-7 items-stretch"
            >
              {filteredProducts.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.45, delay: index * 0.05, ease: "easeOut" }}
                >
                  <Link
                    to={product.out_of_stock ? "#" : `/product/${product.id}`}
                    onClick={(e) => product.out_of_stock && e.preventDefault()}
                    className={`block h-full group ${product.out_of_stock ? "pointer-events-none" : ""}`}
                    data-testid={`product-card-${product.id}`}
                  >
                    <div className="h-full flex flex-col">
                      <div className="lux-frame relative rounded-2xl overflow-hidden aspect-[4/3] bg-[#121212]">
                        {product.image_url ? (
                          <img
                            src={product.image_url}
                            alt={product.name}
                            className={`w-full h-full object-cover group-hover:scale-[1.08] transition-transform duration-[1.1s] ease-out ${product.out_of_stock ? "grayscale opacity-50" : ""}`}
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-[#16130D] to-[#0E0E0F] flex items-center justify-center">
                            <span className="font-playfair italic text-[#D4B483]/30 text-2xl">BB</span>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/45 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        {product.out_of_stock && (
                          <div className="absolute top-3 left-3 bg-[#DC2626] text-white text-[9px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full z-[3]">
                            {t("Out of stock")}
                          </div>
                        )}
                        <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-all duration-400 translate-y-2 group-hover:translate-y-0 z-[3]">
                          <div className="w-10 h-10 rounded-full bg-[#D4B483] flex items-center justify-center shadow-[0_8px_25px_rgba(212,180,131,0.4)]">
                            <Plus className="w-4 h-4 text-[#0A0A0A]" />
                          </div>
                        </div>
                      </div>
                      <div className="pt-4 px-0.5 flex flex-col flex-1">
                        <div className="flex items-baseline">
                          <h3 className="font-playfair text-base md:text-lg text-[#F5F1E8] leading-snug group-hover:text-[#E5CFA5] transition-colors line-clamp-1">{product.name}</h3>
                          <span className="lux-leader hidden sm:block" />
                          <span className="font-mono-price text-gold text-sm md:text-base whitespace-nowrap hidden sm:block">{product.base_price.toFixed(0)} kr</span>
                        </div>
                        <p className="text-[#B4B4BC] text-xs mt-1.5 line-clamp-2 leading-relaxed font-light flex-1">{product.description}</p>
                        <p className="font-mono-price text-gold text-sm mt-2 sm:hidden">{product.base_price.toFixed(0)} kr</p>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        )}
        {!loading && filteredProducts.length === 0 && (
          <div className="text-center py-20">
            <p className="font-playfair italic text-[#A8ABB2] text-lg">{t("No items in this category")}</p>
          </div>
        )}
      </section>
    </div>
  );
}
